import { StepperInput } from 'foundations-components/transitional/components/StepperInput';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { Link } from 'foundations-components/transitional/components/Link';
import { createProductConfig } from 'pricing-pages-lib/utils/productConfigUtils';
import { PriceDisplay } from 'pricing-pages-ui-components/price/PriceDisplay';
import { useEffect, useId, useState } from 'react';
import { SALES, SERVICE } from 'self-service-api/constants/ProductLines';
import SalesServiceSeatExplainerModal from 'ui-addon-upgrades/_core/common/components/seatsModals/SalesServiceSeatExplainerModal';
import { useDisplayedCurrencyCode } from '../../../hooks/useCurrencies';
import { usePriceForQuantityPackQuantity } from '../../../hooks/usePagedQuantityPacks';
import { useAppSelector } from '../../../reducers';
import { calculatedPriceForProductConfigurationsInListSelector } from '../../../selectors/PricingSelectors';
import { hasProductInProductInventorySelector, productsFromProductInventoryQuantityPackQuantitySelector, productsFromProductInventoryQuantitySelector } from '../../../selectors/ProductInventorySelectors';
import { productForSkuIdSelector, skuIdIsQuantityPackSelector, skuSelector } from '../../../selectors/ProductsSelectors';
import { trackAdditionalUnitsPicker, trackModalInteraction } from '../../../utils/tracking/trackingEventUtils';
import { InterstitialSeatsTile } from '../../interstitial/InterstitialSeatsTile';
import { FlushedTitle } from '../../plan-builder/SharedComponents';
import SkeletonLoadWrapper from '../loading/SkeletonLoadWrapper';
import { View } from 'foundations-components/transitional/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SeatsPickerStepper = ({
  additionalUnitSkuId,
  additionalUnitQuantity,
  onChange,
  productConfigurations,
  trackingSource,
  queryParamTerm,
  isInterstitialTile = false,
  disableSteppers = false
}) => {
  const isQuantityPack = useAppSelector(state => skuIdIsQuantityPackSelector(state, {
    skuId: additionalUnitSkuId
  }));
  const {
    includedWithProductVersion,
    includedWithQuantityPack,
    quantityPackApiName,
    productVersionSkuId
  } = useAppSelector(state => {
    if (!isQuantityPack) {
      return {
        includedWithProductVersion: 0,
        includedWithQuantityPack: 0,
        quantityPackApiName: undefined,
        productVersionSkuId: additionalUnitSkuId
      };
    } else {
      const quantityPackSku = skuSelector(state, {
        skuId: additionalUnitSkuId
      });
      const {
        includedWithProductVersion: quantityPackIncludedWithProductVersion,
        includedWithQuantityPack: packagedQuantity,
        quantityPackApiName: quantityPackApiNameScope,
        productVersionSkuId: quantityPackProductVersionSkuId
      } = quantityPackSku;
      return {
        includedWithProductVersion: quantityPackIncludedWithProductVersion,
        includedWithQuantityPack: packagedQuantity,
        quantityPackApiName: quantityPackApiNameScope,
        productVersionSkuId: quantityPackProductVersionSkuId
      };
    }
  });
  const {
    apiName,
    productLine,
    productLevel
  } = useAppSelector(state => productForSkuIdSelector(state, {
    skuId: productVersionSkuId
  }));
  const currencyCode = useDisplayedCurrencyCode();
  const ownedQuantity = useAppSelector(state => quantityPackApiName ? productsFromProductInventoryQuantityPackQuantitySelector(state, {
    productIdentifier: {
      apiName
    },
    quantityPackApiName
  }) : productsFromProductInventoryQuantitySelector(state, {
    skuId: additionalUnitSkuId
  }));
  const minPurchasableSeats = ownedQuantity === 0 ? 1 : ownedQuantity + includedWithProductVersion;
  const ownsProduct = useAppSelector(state => hasProductInProductInventorySelector(state, {
    productAttributeFilters: {
      apiName
    }
  }));
  const ownedUnitQuantity = ownsProduct ? minPurchasableSeats : 0;
  const [salesServiceExplainerModalOpen, setSalesServiceExplainerModalOpen] = useState(false);
  useEffect(() => {
    if (additionalUnitQuantity < ownedQuantity) {
      onChange(ownedQuantity);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [additionalUnitQuantity, ownedQuantity]);
  const additionalUnitPrice = useAppSelector(state => calculatedPriceForProductConfigurationsInListSelector(state, {
    allProductConfigurations: productConfigurations,
    productConfigurations: [createProductConfig({
      skuId: additionalUnitSkuId
    })],
    queryParamTerm,
    displayedCurrencyCode: currencyCode
  }).monthly.calculatedPrice);
  const {
    quantityPackPrice,
    loading: isLoadingStartingPOPPriceForQPs
  } = usePriceForQuantityPackQuantity({
    productApiName: apiName,
    quantityPackApiName,
    additionalQuantity: includedWithQuantityPack,
    // the packaged quantity which amounts to 1 quantityPack
    queryParamTerm
  });
  const additionalUnitDisplayPrice = quantityPackPrice ? Number(quantityPackPrice.discountedPrice.monthlyPrice) : additionalUnitPrice;
  const roundValue = ({
    target: {
      value
    }
  }) => {
    const roundedValue = parseInt(value, 10);
    let nextAdditionalUnitQuantity = roundedValue < minPurchasableSeats ? minPurchasableSeats : roundedValue;
    nextAdditionalUnitQuantity = nextAdditionalUnitQuantity - includedWithProductVersion;
    onChange(nextAdditionalUnitQuantity);
  };
  const handleSeatsChange = event => {
    if (event.target.value) {
      const oldQuantity = additionalUnitQuantity + includedWithProductVersion;
      const newQuantity = parseInt(event.target.value, 10);
      trackAdditionalUnitsPicker({
        action: `stepper ${newQuantity > oldQuantity ? 'increase' : 'decrease'}`,
        trackingSource,
        productLine,
        productLevel,
        ownsProduct,
        skuId: additionalUnitSkuId,
        ownedUnitQuantity,
        displayedUnitQuantity: newQuantity
      });
    }
    roundValue(event);
  };
  const SeatsPickerStepperTitleId = useId();
  const SeatsPickerStepperSubtextId = useId();
  const ariaDescribedBy = [SeatsPickerStepperTitleId, SeatsPickerStepperSubtextId].join(' ');
  // productLine will be filled in for a la carte hubs and unbundled suite
  // For TB sku, there's no productLine. Use quantityPack's quantityPackApiName to effectively
  // get its productLine

  const productLineKey = productLine || quantityPackApiName === 'additional-sales-seats' ? SALES : SERVICE;
  const handleSalesServiceSeatsLinkClick = () => {
    setSalesServiceExplainerModalOpen(true);
    trackModalInteraction({
      action: 'click to open',
      name: `${productLineKey.toLowerCase()}-seat-explainer-modal`,
      value: `${productLineKey[0] + productLineKey.slice(1).toLowerCase()} seats`,
      productLine,
      productLevel,
      trackingSource
    });
  };
  const handleSalesServiceSeatsModalClose = () => {
    setSalesServiceExplainerModalOpen(false);
    trackModalInteraction({
      action: 'click to close',
      name: `${productLineKey.toLowerCase()}-seat-explainer-modal`,
      value: `${productLineKey[0] + productLineKey.slice(1).toLowerCase()} seats`,
      productLine,
      productLevel,
      trackingSource
    });
  };
  if (isInterstitialTile) {
    return /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
      heights: [20],
      widths: [55],
      isLoading: isLoadingStartingPOPPriceForQPs,
      children: /*#__PURE__*/_jsx(InterstitialSeatsTile, {
        productLine: productLineKey,
        price: additionalUnitDisplayPrice,
        description: /*#__PURE__*/_jsx(FormattedMessage, {
          message: productLineKey === SALES ? 'interstitial.salesSeats.description' : 'interstitial.serviceSeats.description'
        }),
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: productLineKey === SALES ? `interstitial.salesSeats.title.${productLevel}` : `interstitial.serviceSeats.title.${productLevel}`,
          options: {
            count: additionalUnitQuantity + includedWithProductVersion
          }
        }),
        children: /*#__PURE__*/_jsx(StepperInput, {
          disabled: disableSteppers,
          "aria-label": `pricingPage.seatsPicker.a11y.select.${productLineKey}`,
          "aria-describedby": ariaDescribedBy,
          value: additionalUnitQuantity + includedWithProductVersion,
          min: minPurchasableSeats,
          onChange: handleSeatsChange,
          "data-selenium-class": "seats-picker-input",
          "data-selenium-id": `${trackingSource}-${apiName}-seat-card-stepper-input`,
          disabledMinButtonTooltip: disableSteppers ? null : /*#__PURE__*/_jsx(FormattedMessage, {
            message: "interstitial.seatsPicker.disabledMinButtonTooltip"
          }),
          disabledMinButtonTooltipProps: {
            placement: 'bottom',
            children: /*#__PURE__*/_jsx(_Fragment, {})
          }
        })
      })
    });
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [salesServiceExplainerModalOpen && /*#__PURE__*/_jsx(SalesServiceSeatExplainerModal, {
      handleModalClose: handleSalesServiceSeatsModalClose,
      productLine: productLineKey
    }), /*#__PURE__*/_jsxs(TFlex, {
      direction: "row",
      justify: "between",
      children: [/*#__PURE__*/_jsx(FlushedTitle, {
        marginBlockEnd: "200",
        children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: `pricingPage.seatsPicker.select.${productLineKey}_jsx`,
          elements: {
            Link: ({
              children
            }) => /*#__PURE__*/_jsx(Link, {
              onClick: handleSalesServiceSeatsLinkClick,
              children: children
            })
          }
        })
      }), /*#__PURE__*/_jsx(StepperInput, {
        "aria-label": `pricingPage.seatsPicker.a11y.select.${productLineKey}`,
        "aria-describedby": ariaDescribedBy,
        value: additionalUnitQuantity + includedWithProductVersion,
        min: minPurchasableSeats,
        onChange: handleSeatsChange,
        "data-selenium-class": "seats-picker-input",
        "data-selenium-id": `${trackingSource}-${apiName}-seat-card-stepper-input`,
        disabled: disableSteppers,
        disabledMinButtonTooltip: disableSteppers ? null : /*#__PURE__*/_jsx(FormattedMessage, {
          message: "interstitial.seatsPicker.disabledMinButtonTooltip"
        }),
        disabledMinButtonTooltipProps: {
          placement: 'bottom',
          children: /*#__PURE__*/_jsx(_Fragment, {})
        }
      })]
    }), /*#__PURE__*/_jsx(View, {
      marginBlockEnd: "400",
      as: "div",
      children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        heights: [20],
        widths: [55],
        isLoading: isLoadingStartingPOPPriceForQPs,
        children: /*#__PURE__*/_jsx(TFlex, {
          direction: "column",
          children: /*#__PURE__*/_jsx("span", {
            id: SeatsPickerStepperSubtextId,
            children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
              message: `pricingPage.seatsPicker.subtext.seats.${productLineKey}_jsx`,
              elements: {
                PriceDisplay
              },
              options: {
                currencyCode,
                price: additionalUnitDisplayPrice
              }
            })
          })
        })
      })
    })]
  });
};
export default SeatsPickerStepper;